export type ContentLanguageVersion = {
  id: number;
  changedAt: string;
  changedByUserId?: number | null;
  changedByName?: string;
  changeNote?: string | null;
  content?: string;
  title?: string;
  detailContent?: string;
};

export type ContentVersionHistoryScope = "company" | "section" | "help";

export function formatContentVersionChangedAt(changedAt: string): string {
  const changedAtDate = changedAt ? new Date(changedAt) : null;
  return changedAtDate && !Number.isNaN(changedAtDate.getTime())
    ? changedAtDate.toLocaleString()
    : "Unknown time";
}

export function formatContentVersionLabel(version: ContentLanguageVersion, isCurrent: boolean): string {
  const dateLabel = formatContentVersionChangedAt(version.changedAt);
  const actor = (version.changedByName || "System").trim() || "System";
  const currentSuffix = isCurrent ? " (current)" : "";
  return `${dateLabel} — ${actor}${currentSuffix}`;
}

export function mergeContentVersionNote(
  versions: ContentLanguageVersion[],
  updated: ContentLanguageVersion,
): ContentLanguageVersion[] {
  return versions.map((version) =>
    version.id === updated.id ? { ...version, changeNote: updated.changeNote ?? null } : version,
  );
}

export function getCurrentContentVersionId(versions: ContentLanguageVersion[]): number | null {
  if (!Array.isArray(versions) || versions.length === 0) return null;
  return versions[0]?.id ?? null;
}

export function isViewingHistoricalContentVersion(
  selectedVersionId: number | null,
  versions: ContentLanguageVersion[],
): boolean {
  const currentVersionId = getCurrentContentVersionId(versions);
  if (currentVersionId == null) return false;
  if (selectedVersionId == null) return false;
  return selectedVersionId !== currentVersionId;
}

export function findContentVersionById(
  versions: ContentLanguageVersion[],
  versionId: number | null,
): ContentLanguageVersion | null {
  if (versionId == null) return versions[0] ?? null;
  return versions.find((version) => version.id === versionId) ?? null;
}
